import { Button, Card, Col, Form, Row } from "antd";
import FullPageLayout from "../../../../templates/FullPageLayout";
import UploadPhoto from "../../../../atoms/UploadPhoto";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
  DateField,
  InputField,
  InputPhone,
  SelectField,
} from "../../../../atoms/FormFields";
import { useNavigate } from "react-router-dom";
import { updateUserById, uploadUserImage } from "../ducks/services";
import { AxiosError } from "axios";
import { RcFile } from "antd/es/upload";
import SuccessMessage from "../../../../atoms/Toasts/SuccessMessage";
import ErrorMessage from "../../../../atoms/Toasts/ErrorMessage";
import { useAppDispatch, useAppSelector } from "../../../../config/hooks";
import {
  getCompanyFormData,
  getUserDetailById,
  removeUserDetailData,
} from "../../../Users/ducks/actions";
import { onImageDelete } from "../../../MyProfile/ducks/services";
import dayjs from "dayjs";
import { NeedHelp } from "../../../../atoms/CustomIcons";

interface FormValues {
  first_name: string;
  last_name: string;
  email: string;
  phone: string;
  phone_office: string;
  first_language: string;
  joined_date: string | dayjs.Dayjs;
  birthday: string | dayjs.Dayjs;
  position_id: string;
  service_line_id?: string[];
  rockstar_at?: string;
}

const tempOptions = [
  { label: "Option 1", value: "Option 1" },
  { label: "Option 2", value: "Option 2" },
  { label: "Option 3", value: "Option 3" },
  { label: "Option 4", value: "Option 4" },
];

export default function EditEmployee() {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const location = window.location.pathname;
  const pathSegments = location.split("/");
  const companyId = pathSegments[pathSegments.length - 1];
  const id = pathSegments[pathSegments.length - 2];

  const {
    control,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<FormValues>();

  const [loading, setLoading] = useState(false);
  const [serviceLines, showServiceLines] = useState(false);
  const [checklistFile, setChecklistFile] = useState<File | null>(null);
  const [uploadModal, setUploadModal] = useState<boolean>(false);
  const companyFormData = useAppSelector(
    (state) => state?.users?.companyFormData
  );
  const userDetailData = useAppSelector(
    (state) => state?.users?.userDetailData
  );
  const [previewImage, setPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });
  const [modalPreviewImage, setModalPreviewImage] = useState<{
    file: string;
    imagePreviewUrl: string | null;
  }>({ file: "", imagePreviewUrl: null });

  const saveUpload = () => {
    setLoading(true);
    setPreviewImage({ ...modalPreviewImage });
    setLoading(false);
    setUploadModal(false);
  };

  const onSubmit = (val: FormValues) => {
    setLoading(true);
    const payload = {
      first_name: val.first_name,
      last_name: val.last_name,
      email: val.email,
      type: "employee",
      phone: val?.phone ? val?.phone : undefined,
      phone_office: val?.phone_office ? val?.phone_office : undefined,
      position_id: val.position_id,
      first_language: val.first_language,
      joined_date: val?.joined_date
        ? dayjs(val.joined_date).toJSON()
        : undefined,
      birthday: val?.birthday ? dayjs(val.birthday).toJSON() : undefined,

      service_line_id:
        val?.service_line_id && val?.service_line_id?.length > 0
          ? val?.service_line_id
          : [],
      rockstar_at: val?.rockstar_at ? val?.rockstar_at : undefined,
      company_id: companyId,
    };

    updateUserById(id, payload)
      .then(() => {
        if (checklistFile && id) {
          uploadUserImage({
            owner_id: id,
            image: checklistFile as File | RcFile,
            image_type: "user_image",
          })
            .then((res) => {
              if (res) {
                setLoading(false);
                SuccessMessage("Employee updated");
                navigate(`/company-settings/employees/${companyId}`);
              }
            })
            .catch((e: unknown) => {
              if (e instanceof AxiosError) {
                setLoading(false);
                ErrorMessage(
                  e?.response?.data?.error?.message ?? "Something went wrong"
                );
              } else {
                setLoading(false);
                ErrorMessage("Something went wrong");
              }
            });
        } else {
          setLoading(false);
          SuccessMessage("Employee updated");
          navigate(`/company-settings/employees/${companyId}`);
        }
      })
      .catch((e: unknown) => {
        if (e instanceof AxiosError) {
          setLoading(false);
          ErrorMessage(
            e?.response?.data?.error?.message ?? "Something went wrong"
          );
        } else {
          setLoading(false);
          ErrorMessage("Something went wrong");
        }
      });
  };

  useEffect(() => {
    if (companyId) {
      dispatch(getCompanyFormData(companyId as string));
    }
  }, [dispatch, companyId]);

  const onChange = () => {
    showServiceLines(true);
  };

  useEffect(() => {
    if (id) {
      dispatch(getUserDetailById(id));
    }
  }, [id, dispatch]);

  useEffect(() => {
    dispatch(removeUserDetailData());
  }, [dispatch]);

  useEffect(() => {
    if (userDetailData?.data) {
      setValue("first_name", userDetailData?.data?.first_name);
      setValue("last_name", userDetailData?.data?.last_name);
      setValue("email", userDetailData?.data?.email);
      setValue("first_language", userDetailData?.data?.first_language);
      setValue("position_id", userDetailData?.data?.position_id);
      if (userDetailData?.data?.position_id) {
        showServiceLines(true);
        setValue(
          "service_line_id",
          userDetailData?.data?.userServiceLines?.map(
            (e: { service_line_id: string }) => e?.service_line_id
          )
        );
        setValue("rockstar_at", userDetailData?.data?.rockstar_at);
      }
      if (userDetailData?.data?.phone) {
        setValue("phone", userDetailData?.data?.phone);
      }
      if (userDetailData?.data?.phone_office) {
        setValue("phone_office", userDetailData?.data?.phone_office);
      }

      if (userDetailData?.data?.birthday) {
        const tempBirthday = dayjs(userDetailData.data.birthday);

        if (tempBirthday.isValid()) {
          setValue("birthday", tempBirthday); // Pass a Day.js object, NOT a string
        }
      }
      if (userDetailData?.data?.joined_date) {
        const tempJoinedDate = dayjs(userDetailData.data.joined_date);

        if (tempJoinedDate.isValid()) {
          setValue("joined_date", tempJoinedDate); // Pass a Day.js object, NOT a string
        }
      }
    }
  }, [userDetailData, setValue, dispatch]);

  function callMembersAPI() {
    dispatch(getUserDetailById(id));
  }

  return (
    <FullPageLayout loading={userDetailData?.loader || loading}>
      <Button className="btn-sm fixedTopBtn">
        <NeedHelp /> Need help?
      </Button>
      <Form
        layout="vertical"
        onFinish={handleSubmit(onSubmit)}
        onKeyDown={(e) => {
          if (e.key === "Enter") e.preventDefault();
        }}
      >
        <Col className="addTeamMember">
          <div
            className="title"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <h2> Edit employee </h2>
          </div>
          <Card>
            <Row gutter={20}>
              <Col span={24}>
                <h4 style={{ marginBottom: "16px" }}>Personal details</h4>
              </Col>
              <Col span={24}>
                <UploadPhoto
                  saveUpload={saveUpload}
                  loading={loading}
                  uploadModal={uploadModal}
                  setUploadModal={setUploadModal}
                  imageUploaded={userDetailData?.data?.image_url}
                  onImageDelete={() => {
                    onImageDelete(
                      setLoading,
                      SuccessMessage,
                      callMembersAPI,
                      ErrorMessage,
                      userDetailData?.data?.image_url
                        ? userDetailData?.data?.image_id
                        : undefined
                    );
                  }}
                  previewImage={previewImage}
                  checklistFile={checklistFile}
                  modalPreviewImage={modalPreviewImage}
                  setChecklistFile={setChecklistFile}
                  setPreviewImage={setPreviewImage}
                  setModalPreviewImage={setModalPreviewImage}
                  //   setIsCropModalOpen={setIsCropModalOpen}
                />
              </Col>

              <Col span={12}>
                <InputField
                  fieldName="first_name"
                  label="First name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter first name",
                  }}
                  isRequired
                  rules={{
                    required: "First name is required.",
                  }}
                  validate={errors.first_name && "error"}
                  validMessage={errors.first_name && errors.first_name.message}
                />
              </Col>
              <Col span={12}>
                <InputField
                  fieldName="last_name"
                  label="Last name"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter last name",
                  }}
                  isRequired
                  rules={{
                    required: "Last name is required.",
                  }}
                  validate={errors.last_name && "error"}
                  validMessage={errors.last_name && errors.last_name.message}
                />
              </Col>
              <Col span={12}>
                <InputField
                  fieldName="email"
                  label="Account email"
                  control={control}
                  defaultValue=""
                  iProps={{
                    placeholder: "Enter account email",
                  }}
                  isRequired
                  rules={{
                    required: "Account email is required.",
                    pattern: {
                      value:
                        /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                      message: "Incorrect email address.",
                    },
                  }}
                  validate={errors.email && "error"}
                  validMessage={errors.email && errors.email.message}
                  tooltipTitle="This email is user’s account email in LandScaling."
                />
              </Col>
              <Col span={12}>
                <SelectField
                  fieldName="first_language"
                  label="First language"
                  control={control}
                  selectOption={[
                    { label: "English", value: "english" },
                    { label: "Spanish", value: "spanish" },
                  ]}
                  defaultValue="english"
                  iProps={{ placeholder: "Select language" }}
                  isRequired
                  rules={{
                    required: "First language is required.",
                  }}
                  validate={errors.first_language && "error"}
                  validMessage={
                    errors.first_language && errors.first_language.message
                  }
                />
              </Col>

              <Col span={12}>
                <InputPhone
                  fieldName="phone"
                  label="Mobile phone"
                  control={control}
                />
              </Col>
              <Col span={12}>
                <InputPhone
                  fieldName="phone_office"
                  label="Office phone"
                  control={control}
                />
              </Col>
              <Col span={12} className="mb-0">
                <DateField
                  fieldName="joined_date"
                  label="Joined company"
                  control={control}
                />
              </Col>
              <Col span={12} className="mb-0">
                <DateField
                  fieldName="birthday"
                  label="Birthdate"
                  control={control}
                />
              </Col>
            </Row>
          </Card>
          <Card>
            <Row gutter={20}>
              <Col span={24}>
                <h4 style={{ marginBottom: "16px" }}> Job details </h4>
              </Col>
              <Col span={24}>
                <SelectField
                  fieldName="position_id"
                  label="Position"
                  control={control}
                  selectOption={companyFormData?.position}
                  iProps={{ placeholder: "Select position" }}
                  onSelectChange={onChange}
                  isRequired
                  rules={{
                    required: "Position is required.",
                  }}
                  validate={errors.position_id && "error"}
                  validMessage={
                    errors.position_id && errors.position_id.message
                  }
                />
              </Col>
              {serviceLines && (
                <>
                  <Col span={24}>
                    <SelectField
                      fieldName="service_line_id"
                      label="Service lines"
                      control={control}
                      selectOption={companyFormData?.service_line}
                      iProps={{
                        placeholder: "Select service lines",
                        mode: "multiple",
                      }}
                    />
                  </Col>
                  <Col span={24} className="mb-0">
                    <SelectField
                      fieldName="rockstar_at"
                      label="Rockstar at"
                      control={control}
                      selectOption={tempOptions}
                      iProps={{
                        placeholder: "Select rockstar at",
                        // mode: "multiple",
                      }}
                    />
                  </Col>
                </>
              )}
            </Row>
          </Card>
        </Col>
        <div className="fixed_btm_card">
          <Card>
            <div className="centered">
              <Button
                htmlType="button"
                className="outlined-btn"
                onClick={() =>
                  navigate(`/company-settings/employees/${companyId}`)
                }
              >
                Cancel
              </Button>
              <div style={{ display: "flex", gap: "16px" }}>
                <Button
                  htmlType="submit"
                  type="primary"
                  className="mint-btn"
                  // disabled={true}
                >
                  Save changes
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </Form>
    </FullPageLayout>
  );
}
